/**
 * Global Real-Time Move Cache Hydration Provider
 *
 * This component uses the generated Apollo streaming subscription hook to keep high-frequency
 * move fields continuously updated in the Apollo cache. Uses streaming to only receive
 * delta updates, reducing server egress.
 *
 * Components simply use cache-first queries and automatically receive updates
 * as this subscription hydrates the cache.
 */

import React, { useMemo, useState } from 'react';
import { Cursor_Ordering } from '@gql/base-types';
import { useGlobalRealtimeMoveUpdatesSubscription } from '../features/timeline/gql/globalRealtimeMoveUpdates.subscription.generated';

interface GlobalSubscriptionProviderProps {
  children: React.ReactNode;
}

export const GlobalSubscriptionProvider: React.FC<GlobalSubscriptionProviderProps> = ({ children }) => {
  // Calculate time range: +/- 3 days from now
  const timeVariables = useMemo(() => {
    const now = new Date();
    const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

    return {
      startTime: threeDaysAgo.toISOString(),
      endTime: threeDaysFromNow.toISOString(),
    };
  }, []);

  // Initialize cursor to start streaming from 15 minutes ago to catch recent updates
  // This helps with the unreliable updatedat timestamp issue
  const initialCursor = useMemo(() => {
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    return [
      {
        initial_value: { updatedat: fifteenMinutesAgo.toISOString() },
        ordering: Cursor_Ordering.Asc,
      },
    ];
  }, []);

  const [deltaCount, setDeltaCount] = useState(0);

  // Use the generated streaming subscription hook
  const { data, loading, error } = useGlobalRealtimeMoveUpdatesSubscription({
    variables: {
      cursor: initialCursor,
      ...timeVariables,
    },
    errorPolicy: 'all', // Continue even if there are some errors
    onSubscriptionData: ({ subscriptionData }) => {
      if (subscriptionData.data?.moves_stream) {
        const moves = subscriptionData.data.moves_stream;
        const moveCount = moves.length;
        // Calculate size of received data
        const dataStr = JSON.stringify(moves);
        const byteSize = new TextEncoder().encode(dataStr).length;

        // Get compressed size if available from transport
        const compressedSize = (subscriptionData as any).extensions?.compressed_size;

        // console.log(
        //   `[GlobalSubscriptionProvider] Received ${moveCount} moves, ` +
        //     `Size: ${byteSize} bytes` +
        //     (compressedSize ? `, Compressed: ${compressedSize} bytes` : '')
        // );

        if (moveCount > 0) {
          setDeltaCount(prev => prev + moveCount);
          moves.forEach(async move => {
            // console.log(
            //   `[GlobalSubscriptionProvider] 🔥 STREAMING DELTA: Move ${move.id} change detected. New status: ${move.status}`
            // );
          });
          // Apollo Client automatically updates the cache with the new data
          // No manual cache manipulation needed - components will re-render automatically
        }
      }
    },
    onSubscriptionComplete: () => {
      //console.log('[GlobalSubscriptionProvider] Streaming subscription completed');
    },
  });

  // Simple error logging
  React.useEffect(() => {
    if (error) {
      console.error('[GlobalSubscriptionProvider] Subscription error:', error);
      // Silent failure - app continues to work with cache-first queries
    }
  }, [error]);

  React.useEffect(() => {
    //console.log('[GlobalSubscriptionProvider] 🎯 STREAMING SUBSCRIPTION started');
    return () => {
      //console.log(`[GlobalSubscriptionProvider] 📊 Total delta updates received: ${deltaCount}`);
    };
  }, [deltaCount]);

  // This component renders its children - the subscription runs as a side effect
  return <>{children}</>;
};

export default GlobalSubscriptionProvider;
